import { Box, Flex, Icon, Spinner, Text, useBreakpointValue } from '@chakra-ui/react';
import { FaCheckCircle } from '@react-icons/all-files/fa/FaCheckCircle';

import Button from '../../../../components/ui/buttons/Button';
import { sameEmail } from '../../../../utils/customer';
import { capitalizeFirstLetter, initialName } from '../../../../utils/strings';
import { DealStateEnum } from '../../../../utils/types/deal';
import { DealWithFarthestState } from '../../utils';

const MOBILE_NAME_CHAR_LIMIT = 14;

type NameParts = { first_name?: string | null; last_name?: string | null } | null | undefined;

const fullName = (c: NameParts) =>
  `${capitalizeFirstLetter(c?.first_name)} ${capitalizeFirstLetter(c?.last_name)}`.trim();

type RowStatus = 'not_ready' | 'unsigned' | 'in_progress' | 'signed';

type Props = {
  deal: DealWithFarthestState;
  startSigning: (isCobuyerOverride?: boolean) => void;
};

const getRowStatus = (
  sentForSignatures: boolean,
  finishedSigning?: boolean | null,
  signatureData?: unknown,
): RowStatus => {
  if (!sentForSignatures) return 'not_ready';
  if (finishedSigning) return 'signed';
  if (signatureData) return 'in_progress';
  return 'unsigned';
};

const RowAction = ({ status, onSign }: { status: RowStatus; onSign: () => void }) => {
  switch (status) {
    case 'not_ready':
      return (
        <Flex align="center" gap={2} color="grayDisabledText">
          <Spinner size="xs" speed="1s" />
          <Text fontSize="12px" fontWeight="semibold">
            PREPARING
          </Text>
        </Flex>
      );
    case 'signed':
      return (
        <Flex align="center" gap={2}>
          <Icon as={FaCheckCircle} color="successGreen" boxSize="16px" />
          <Text fontSize="12px" color="successGreen" fontWeight="bold">
            SIGNED
          </Text>
        </Flex>
      );
    case 'in_progress':
      return (
        <Button size="sm" whiteSpace="nowrap" w={{ base: 'full', md: 'auto' }} onClick={onSign}>
          CONTINUE SIGNING
        </Button>
      );
    case 'unsigned':
      return (
        <Button size="sm" whiteSpace="nowrap" w={{ base: 'full', md: 'auto' }} onClick={onSign}>
          REVIEW & SIGN
        </Button>
      );
    default:
      return null;
  }
};

type SignerRowProps = {
  name: string;
  signerRole: string;
  status: RowStatus;
  onSign: () => void;
};

const SignerRow = ({ name, signerRole, status, onSign }: SignerRowProps) => {
  return (
    <Flex py={2} alignItems="center" justify="space-between" gap={{ base: 4, md: 2 }}>
      <Flex direction="column" flex={1} minW={0}>
        <Text fontSize="14px" fontWeight="bold" color="gray.800">
          {name}
        </Text>
        <Text
          fontSize="11px"
          fontWeight="semibold"
          color="gray.500"
          textTransform="uppercase"
          letterSpacing="wider"
        >
          {signerRole}
        </Text>
      </Flex>
      <Flex flexShrink={0} w="65%" justify="flex-end">
        <RowAction status={status} onSign={onSign} />
      </Flex>
    </Flex>
  );
};

const InlineSigningList = ({ deal, startSigning }: Props) => {
  const sentForSignatures = deal?.state === DealStateEnum.SentForSignatures;
  const hasSharedEmail = sameEmail(deal?.customer, deal?.cobuyer);

  const buyerStatus = getRowStatus(
    sentForSignatures,
    deal?.customer?.finished_signing,
    deal?.customer?.signature_data,
  );
  const cobuyerStatus = getRowStatus(
    sentForSignatures,
    deal?.cobuyer?.finished_signing,
    deal?.cobuyer?.signature_data,
  );

  const shouldTruncate =
    useBreakpointValue({
      base:
        fullName(deal?.customer).length > MOBILE_NAME_CHAR_LIMIT ||
        fullName(deal?.cobuyer).length > MOBILE_NAME_CHAR_LIMIT,
      md: false,
    }) ?? false;

  if (!hasSharedEmail) return null;

  const buyerName = shouldTruncate ? initialName(deal?.customer) : fullName(deal?.customer);
  const cobuyerName = shouldTruncate ? initialName(deal?.cobuyer) : fullName(deal?.cobuyer);

  return (
    <Box
      borderRadius="md"
      opacity={buyerStatus === 'not_ready' && cobuyerStatus === 'not_ready' ? 0.55 : 1}
    >
      <SignerRow
        name={buyerName}
        signerRole="BUYER"
        status={buyerStatus}
        onSign={() => startSigning(false)}
      />
      <Box borderTop="1px solid" borderColor="gray.200" />
      <SignerRow
        name={cobuyerName}
        signerRole="CO-BUYER"
        status={cobuyerStatus}
        onSign={() => startSigning(true)}
      />
    </Box>
  );
};

export default InlineSigningList;
